import { useEffect, useState } from "react";
import { TextField } from "@radix-ui/themes";

import type { NumberFieldProps } from "./index.types";

const decimalPlaces = (step: number): number => {
  const [, fraction = ""] = String(step).split(".");
  return fraction.length;
};

export function NumberField({ ariaLabel, disabled, maximum, minimum, onValueChange, step, unit, value }: NumberFieldProps) {
  const [draft, setDraft] = useState(value === "" ? "" : String(value));

  useEffect(() => {
    setDraft(value === "" ? "" : String(value));
  }, [value]);

  const commit = (candidate: number) => {
    const next = Math.min(maximum, Math.max(minimum, candidate));
    const normalized = Number(next.toFixed(decimalPlaces(step)));
    setDraft(String(normalized));
    onValueChange(normalized);
  };

  const changeBy = (direction: -1 | 1) => {
    const current = value === "" ? minimum : value;
    // Decimal steps are normalized so repeated clicks do not accumulate floating-point noise.
    commit(current + (step * direction));
  };

  return (
    <div className="number-field">
      <TextField.Root
        aria-label={ariaLabel}
        aria-valuemax={maximum}
        aria-valuemin={minimum}
        aria-valuenow={value === "" ? undefined : value}
        disabled={disabled}
        inputMode={step < 1 ? "decimal" : "numeric"}
        onBlur={() => {
          const parsed = Number(draft.replace(",", "."));
          if (draft.trim() === "" || !Number.isFinite(parsed)) setDraft(value === "" ? "" : String(value));
          else commit(parsed);
        }}
        onChange={(event) => {
          const raw = event.target.value;
          if (!/^\d*(?:[.,]\d*)?$/u.test(raw)) return;
          setDraft(raw);
          if (!raw || raw.endsWith(".") || raw.endsWith(",")) return;
          const parsed = Number(raw.replace(",", "."));
          if (Number.isFinite(parsed)) onValueChange(parsed);
        }}
        onKeyDown={(event) => {
          if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
          event.preventDefault();
          changeBy(event.key === "ArrowUp" ? 1 : -1);
        }}
        role="spinbutton"
        type="text"
        value={draft}
      >
        {unit ? <TextField.Slot side="right"><span className="number-field-unit">{unit}</span></TextField.Slot> : null}
      </TextField.Root>
    </div>
  );
}
